import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import {
    Order,
    OrderCreateInput,
    OrderExecutionResult,
    OrderFilters,
    OrderSummary,
    TradingCalculation,
} from '../../models/order';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '../types';

// List orders with enhanced filtering for forex
const fetchOrders = async (filters?: OrderFilters) => {
    const { data } = await httpClient.get<PaginatedResponse<Order>>(
        API_ENDPOINTS.TRADER.ORDERS.LIST,
        { params: filters }
    );
    return data;
};

export const useOrders = (filters?: OrderFilters) => {
    return useQuery<PaginatedResponse<Order>, Error>({
        queryKey: [API_ENDPOINTS.TRADER.ORDERS.LIST, filters],
        queryFn: () => fetchOrders(filters),
    });
};

// Get order history
const fetchOrderHistory = async (filters?: OrderFilters) => {
    const { data } = await httpClient.get<PaginatedResponse<Order>>(
        API_ENDPOINTS.TRADER.ORDERS.HISTORY,
        { params: filters }
    );
    return data;
};

export const useOrderHistory = (filters?: OrderFilters) => {
    return useQuery<PaginatedResponse<Order>, Error>({
        queryKey: [API_ENDPOINTS.TRADER.ORDERS.HISTORY, filters],
        queryFn: () => fetchOrderHistory(filters),
    });
};

// Get order summary statistics
const fetchOrderSummary = async (period?: string) => {
    const { data } = await httpClient.get<ApiResponse<OrderSummary>>(
        API_ENDPOINTS.TRADER.ORDERS.SUMMARY,
        { params: { period } }
    );
    return data.data;
};

export const useOrderSummary = (period?: string) => {
    return useQuery<OrderSummary, Error>({
        queryKey: [API_ENDPOINTS.TRADER.ORDERS.SUMMARY, period],
        queryFn: () => fetchOrderSummary(period),
    });
};

// Get specific order by ID
const fetchOrder = async (id: string) => {
    const { data } = await httpClient.get<ApiResponse<Order>>(
        API_ENDPOINTS.TRADER.ORDERS.DETAILS(id)
    );
    return data.data;
};

export const useOrder = (id: string) => {
    return useQuery<Order, Error>({
        queryKey: [API_ENDPOINTS.TRADER.ORDERS.DETAILS(id)],
        queryFn: () => fetchOrder(id),
        enabled: !!id,
    });
};

// Calculate order parameters before placing
const calculateOrder = async (params: {
    instrument_id: string;
    trading_account_id: string;
    side: 'buy' | 'sell';
    lot_size: number;
    leverage: number;
    price?: number;
}) => {
    const { data } = await httpClient.post<ApiResponse<TradingCalculation>>(
        API_ENDPOINTS.TRADER.ORDERS.CALCULATE(params.instrument_id),
        params
    );
    return data.data;
};

export const useCalculateOrder = () => {
    return useMutation<
        TradingCalculation,
        Error,
        {
            instrument_id: string;
            trading_account_id: string;
            side: 'buy' | 'sell';
            lot_size: number;
            leverage: number;
            price?: number;
        }
    >({
        mutationFn: calculateOrder,
    });
};

// Create order with enhanced forex features
export const useCreateOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<OrderExecutionResult, Error, OrderCreateInput>({
        mutationFn: async input => {
            try {
                const { data } = await httpClient.post<ApiResponse<OrderExecutionResult>>(
                    API_ENDPOINTS.TRADER.ORDERS.CREATE,
                    input
                );
                return data.data;
            } catch (error: any) {
                // Log the error for debugging
                console.error('Order creation error:', {
                    input,
                    error: error.message,
                    status: error.status,
                    data: error.data,
                });

                // Re-throw the error with enhanced information
                throw error;
            }
        },
        onSuccess: () => {
            // Invalidate all trader trade queries to refresh all trade lists
            queryClient.invalidateQueries({ queryKey: ['trader-trades'] });
            // Also invalidate any trade summary queries
            queryClient.invalidateQueries({ queryKey: ['trader-trades-summary'] });
        },
        onError: (error: any) => {
            // Log mutation errors
            console.error('Mutation error in useCreateOrder:', error);
        },
    });
};

// Update order (modify price, lot size, stop loss, take profit)
export const useUpdateOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<
        Order,
        Error,
        {
            id: string;
            input: {
                price?: number;
                lot_size?: number;
                stop_loss_price?: number;
                take_profit_price?: number;
            };
        }
    >({
        mutationFn: async ({ id, input }) => {
            const { data } = await httpClient.put<ApiResponse<Order>>(
                API_ENDPOINTS.TRADER.ORDERS.UPDATE(id),
                input
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.ORDERS] });
        },
    });
};

// Cancel order
export const useCancelOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<Order, Error, string>({
        mutationFn: async id => {
            const { data } = await httpClient.delete<ApiResponse<Order>>(
                API_ENDPOINTS.TRADER.ORDERS.CANCEL(id)
            );
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.ORDERS] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.ME] });
        },
    });
};

// Legacy support for backward compatibility
export const useAllOrders = (params?: {
    status?: string;
    type?: string;
    side?: string;
    trading_pair_id?: string;
    instrument_id?: string;
    page?: number;
    per_page?: number;
}) => {
    const filters: OrderFilters = {
        status: params?.status as any,
        instrument_id: params?.instrument_id || params?.trading_pair_id,
        // Convert other params as needed
    };

    return useOrders(filters);
};

// Close order (full or partial)
export const useCloseOrder = () => {
    const queryClient = useQueryClient();
    return useMutation<
        { success: boolean; message: string; data: any },
        Error,
        {
            id: string;
            close_type: 'full' | 'partial';
            close_amount?: number;
        }
    >({
        mutationFn: async ({ id, close_type, close_amount }) => {
            const payload: any = { close_type };
            if (close_type === 'partial' && close_amount) {
                payload.close_amount = close_amount;
            }

            const { data } = await httpClient.post<
                ApiResponse<{ success: boolean; message: string; data: any }>
            >(API_ENDPOINTS.TRADER.ORDERS.CLOSE(id), payload);
            return data.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.ORDERS] });
            queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.TRADER.BALANCE.ME] });
        },
    });
};
